import { faEdit } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Authorization from '../../Authentication/Authorization'
import DialogContext from '../../Authentication/DialogContext'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import TokenDatabaseService from '../../service/TokenDatabaseService'
import GatewayType from '../GatewayType'

const TokenDatabase: GatewayType = () => {
  const { t } = useTranslation()
  const [tokenDatabase, setTokenDatabase] = useState({} as any)
  const [loading, setLoading] = useState(true)
  const { editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    TokenDatabaseService.getTokenDatabase()
      .then((tokenDatabase) => {
        setTokenDatabase(tokenDatabase)
      })
      .finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: 'label',
        dataField: 'label',
        classes: 'font-weight-bolder',
        sort: true,
      },
      {
        text: 'value',
        dataField: 'value',
        sort: true,
      },
    ]
  }, [])

  const data = getData(tokenDatabase, t)

  return (
    <Loading isLoading={loading}>
      <>
        <DataTable
          keyField='label'
          data={data}
          columns={columns}
          headerClasses='d-none'
          searchable={false}
          caption={t('tokenDatabase.text')}
          helpLink={t('tokenDatabase.helpLink')}
        />
        <div className='text-right pr-1 ml-nfc-1'>
          <ProtectedButton
            variant='primary'
            className='ml-auto my-auto'
            onClick={() => updateTokenDatabaseInfo(t, editDialogPropsSetter, setTokenDatabase, tokenDatabase)}
            permissions={Authorization.settings.tokenDatabase.edit}>
            <FontAwesomeIcon icon={faEdit} /> <span className='ml-1 my-auto'>{t('edit')}</span>
          </ProtectedButton>
        </div>
      </>
    </Loading>
  )
}

function getData(config: any, t: TFunction) {
  return Object.keys(config).length > 0
    ? [
        {
          label: t('databaseType'),
          value: config.databaseType,
        },
        {
          label: t('useTls'),
          value: config.useTls,
        },
        {
          label: t('username.text'),
          value: config.username,
        },
        {
          label: t('password.text'),
          value: config.password ? '<Protected>' : '',
        },
        {
          label: t('databaseName'),
          value: config.databaseName,
        },
        {
          label: t('databaseServer'),
          value: config.databaseServer,
        },
        {
          label: t('port.text'),
          value: config.port,
        },
      ]
    : []
}

function updateTokenDatabaseInfo(t: TFunction, editDialogPropsSetter: any, setLicense: React.Dispatch<any>, config: any) {
  const props: EditDialogProps = {
    show: true,
    header: t('tokenDatabase.editHeader'),
    labelGridSize: 3,
    onSubmit: TokenDatabaseService.updateTokenDatabase,
    onSubmitExtraArgs: [config, t, setLicense],
    data: [
      {
        id: 'databaseType',
        type: 'select',
        options: ['MARIADB', 'MYSQL_NATIVE', 'SQL_SERVER', 'ORACLE'],
        value: config.databaseType,
        label: t('databaseType'),
        required: true,
      },
      {
        groupLabel: t('useTls'),
        groupName: 'useTls',
        type: 'switch',
        checkboxes: [
          {
            id: 'useTls',
            value: config.useTls,
          },
        ],
      },
      {
        id: 'username',
        value: config.username,
        type: 'text',
        label: t('username.text'),
        placeholder: t('username.text'),
        autoComplete: 'username',
        required: true,
        requiredMessage: t('invalidMessage', { label: t('username.text') }),
      },
      {
        id: 'password',
        value: config.password ? '<Protected>' : '',
        type: 'text',
        label: t('password.text'),
        placeholder: t('password.text'),
        autoComplete: 'new-password',
        required: true,
        requiredMessage: t('invalidMessage', { label: t('password.text') }),
      },
      {
        id: 'databaseName',
        value: config.databaseName,
        type: 'text',
        label: t('databaseName'),
        placeholder: t('databaseName'),
        autoComplete: 'databaseName',
        required: true,
        requiredMessage: t('invalidMessage', { label: t('databaseName') }),
      },
      {
        id: 'databaseServer',
        value: config.databaseServer,
        type: 'text',
        label: t('databaseServer'),
        placeholder: t('databaseServer'),
        autoComplete: 'databaseServer',
        required: true,
        requiredMessage: t('invalidMessage', { label: t('databaseName') }),
      },
      {
        id: 'port',
        value: config.port,
        type: 'number',
        label: t('port.text'),
        placeholder: t('port.text'),
        autoComplete: 'port',
        required: true,
        requiredMessage: t('invalidMessage', { label: t('port.text') }),
      },
    ],
  }

  editDialogPropsSetter(props)
}

export default TokenDatabase
